import * as turf from '@turf/turf';
import type { Feature, Geometry, MultiPolygon, Polygon } from 'geojson';
import { geoServer, geoServerDefaultParams } from '../../../services/geoServer';
import { computeBbox } from '../../../utils/geo';
import { ErrorLogger } from './errorHandling';

export const MUNICIPIOS_PE_TYPENAME = 'pepe-atlas:municipios_pe';
export const MUNICIPALITY_NAME_FIELD = 'nm_mun';

interface MunicipioProperties {
  [MUNICIPALITY_NAME_FIELD]: string;
}

interface MunicipiosFeatureCollection {
  type: 'FeatureCollection';
  features: Array<Feature<Geometry, MunicipioProperties>>;
}

function isPolygonal(geometry: Geometry): geometry is Polygon | MultiPolygon {
  return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
}

// Não usa ApiReverseGeocoding: é baseada em ponto único (não cobre múltiplos
// municípios) e depende de infraestrutura indisponível no momento.
export async function findPredominantMunicipality(
  geometry: Geometry
): Promise<string | null> {
  if (!isPolygonal(geometry)) return null;

  const bbox = computeBbox({ type: 'Feature', geometry, properties: {} });
  if (!bbox) return null;

  const [west, south, east, north] = bbox;

  try {
    const response = await geoServer.get<MunicipiosFeatureCollection>('wfs', {
      params: {
        ...geoServerDefaultParams,
        typename: MUNICIPIOS_PE_TYPENAME,
        srsName: 'EPSG:4326',
        bbox: `${west},${south},${east},${north}`,
      },
    });

    const areaFeature = turf.feature(geometry);

    let predominantName: string | null = null;
    let largestIntersectionArea = 0;

    for (const municipio of response.data.features) {
      const name = municipio.properties[MUNICIPALITY_NAME_FIELD];
      if (!name || !isPolygonal(municipio.geometry)) continue;

      try {
        const intersection = turf.intersect(
          turf.featureCollection([areaFeature, turf.feature(municipio.geometry)])
        );
        if (!intersection) continue;

        const intersectionArea = turf.area(intersection);
        if (intersectionArea > largestIntersectionArea) {
          largestIntersectionArea = intersectionArea;
          predominantName = name;
        }
      } catch (error) {
        ErrorLogger.logWarning(
          `Failed to intersect with municipality "${name}": ${(error as Error).message}`,
          'municipalityLookup'
        );
      }
    }

    return predominantName;
  } catch (error) {
    ErrorLogger.logWarning(
      `Failed to fetch municipios_pe for predominant municipality lookup: ${(error as Error).message}`,
      'municipalityLookup'
    );
    return null;
  }
}
